import React from 'react';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Stack,
    Typography
} from '@mui/material';
import { CheckCircle as SelectedIcon } from '@mui/icons-material';

function ProductCard({
    selected,
    title,
    subtitle,
    price,
    badges = [],
    chips = [],
    badgeText,
    badgeColor = 'primary',
    recommended = false,
    hot = false,
    highlightNote,
    onClick,
    disabled = false,
    tone = 'default',
    children
}) {
    const toneStyles = {
        default: {},
        primary: { borderColor: 'primary.light', bgcolor: 'background.paper' },
        secondary: { borderColor: 'grey.300', bgcolor: 'grey.50' }
    };
    const selectedStyles = selected ? {
        borderColor: 'success.dark',
        borderWidth: 3,
        bgcolor: 'rgba(46, 125, 50, 0.16)',
        boxShadow: '0 0 0 2px rgba(46, 125, 50, 0.20), 0 8px 22px rgba(46, 125, 50, 0.16)'
    } : {};
    const visibleBadges = (badges || []).filter((badge) => badge?.text);

    return (
        <Card
            className={selected ? 'bundle-builder-product-card-selected' : undefined}
            variant={selected ? 'elevation' : 'outlined'}
            elevation={selected ? 4 : 0}
            sx={{
                height: '100%',
                opacity: disabled ? 0.6 : 1,
                borderColor: 'divider',
                borderWidth: 1,
                bgcolor: 'background.paper',
                transition: 'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
                ...toneStyles[tone],
                ...selectedStyles,
                '&:hover': {
                    borderColor: disabled ? 'divider' : selected ? 'success.dark' : 'primary.main'
                }
            }}
        >
            <CardActionArea disabled={disabled} onClick={onClick} sx={{ height: '100%', alignItems: 'stretch' }}>
                <CardContent sx={{ height: '100%', p: { xs: 1.5, sm: 2 } }}>
                    <Stack spacing={1.25}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {selected && <Chip icon={<SelectedIcon />} label="Selected" size="small" color="success" />}
                            {visibleBadges.length > 0 ? visibleBadges.map((badge, index) => (
                                <Chip
                                    key={`${badge.text}-${index}`}
                                    label={badge.text}
                                    size="small"
                                    color={badge.type || badge.color || badgeColor}
                                    variant={selected ? 'filled' : 'outlined'}
                                />
                            )) : badgeText && (
                                <Chip label={badgeText} size="small" color={badgeColor} variant={selected ? 'filled' : 'outlined'} />
                            )}
                            {recommended && !badgeText && visibleBadges.length === 0 && (
                                <Chip label="Recommended" size="small" color="success" variant={selected ? 'filled' : 'outlined'} />
                            )}
                            {hot && !badgeText && visibleBadges.length === 0 && (
                                <Chip label="HOT" size="small" color="error" variant={selected ? 'filled' : 'outlined'} />
                            )}
                        </Stack>
                        <Box display="flex" justifyContent="space-between" gap={2}>
                            <Box>
                                <Typography variant="subtitle1">{title}</Typography>
                                {subtitle && (
                                    <Typography variant="body2" color="text.secondary">
                                        {subtitle}
                                    </Typography>
                                )}
                            </Box>
                            {price && (
                                <Typography variant="h6" color="primary.main" sx={{ whiteSpace: 'nowrap' }}>
                                    {price}
                                </Typography>
                            )}
                        </Box>
                        {chips.length > 0 && (
                            <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                                {chips.map((chip) => (
                                    <Chip key={chip} label={chip} size="small" variant="outlined" />
                                ))}
                            </Stack>
                        )}
                        {highlightNote && (
                            <Typography variant="caption" color={selected ? 'primary.main' : 'text.secondary'}>
                                {highlightNote}
                            </Typography>
                        )}
                        {children}
                    </Stack>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

export default ProductCard;
